export type MetaEventParameters = Record<string, string | number | string[] | undefined>;

type Fbq = ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string; push?: Fbq };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const seenEvents = new Set<string>();
let initialized = false;

const debug = (eventName: string, eventId?: string) => {
  if (process.env.NEXT_PUBLIC_META_TRACKING_DEBUG === "true") {
    console.debug("[meta-tracking]", { eventName, eventId });
  }
};

const getPixelId = () => process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim();

const ensureFbq = () => {
  if (typeof window === "undefined") return undefined;
  if (window.fbq) return window.fbq;
  const fbq = ((...args: unknown[]) => {
    fbq.queue = fbq.queue || [];
    fbq.queue.push(args);
  }) as Fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
};

export const metaPixel = {
  isConfigured: () => typeof window !== "undefined" && Boolean(getPixelId()),
  init: () => {
    const fbq = ensureFbq();
    const pixelId = getPixelId();
    if (!fbq || !pixelId || initialized) return;
    initialized = true;
    fbq("init", pixelId);
  },
  pageView: () => {
    if (!initialized || !window.fbq) return;
    window.fbq("track", "PageView");
    debug("PageView");
  },
  trackStandardEvent: (eventName: string, parameters: MetaEventParameters, eventId?: string) => {
    if (!initialized || !window.fbq) return;
    window.fbq("track", eventName, parameters, eventId ? { eventID: eventId } : undefined);
    debug(eventName, eventId);
  },
  trackCustomEvent: (eventName: string, parameters: MetaEventParameters, eventId?: string) => {
    if (!initialized || !window.fbq) return;
    window.fbq("trackCustom", eventName, parameters, eventId ? { eventID: eventId } : undefined);
    debug(eventName, eventId);
  },
  once: (key: string, callback: () => void) => {
    if (seenEvents.has(key)) return;
    try {
      if (sessionStorage.getItem(`sr_meta_event:${key}`)) {
        seenEvents.add(key);
        return;
      }
      sessionStorage.setItem(`sr_meta_event:${key}`, "1");
    } catch {
      // Tracking must never break booking when storage is unavailable.
    }
    seenEvents.add(key);
    callback();
  },
};
